import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { Skin } from '@core/interfaces/skin.interface';
import { UpgraderSkin } from '../interfaces/upgrader-skin.interface';
import { SessionService } from '@core/services/session.service';
import { SocketService } from '@core/services/socket.service';
import { normalizeSkinImage } from '@core/utils/skins.util';
import { DEFAULT_SKIN_COLOR } from '../upgrader.constants';
import { ToastrService } from 'ngx-toastr';

export type UpgraderState = 'idle' | 'rolling' | 'won' | 'lost';

/** Raw skin data from API before transformation */
export interface RawSkinResponse {
  assetid: string;
  price: number;
  image: string;
  name?: string;
  market_hash_name?: string;
  color?: string;
  name_color?: string;
  type?: string;
  tag?: string;
  effect?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UpgraderNewService {
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastrService = inject(ToastrService);
  readonly session = inject(SessionService);

  // ============ State Signals ============
  readonly state = signal<UpgraderState>('idle');
  readonly isProcessing = signal(false);
  readonly isFastMode = signal(false);

  // User's won skins (displayed on left side)
  readonly userInventorySkins = signal<Skin[]>([]);

  // Selected target skins (right side)
  readonly selectedTargetSkins = signal<Skin[]>([]);

  // Balance wagering
  readonly wagerAmount = signal<number>(0);

  // Offset slider (0 to 1, where 0.5 is center)
  readonly offset = signal<number>(0.5);

  // Client seed for provably fair
  readonly clientSeed = signal<string | null>(null);

  // Last roll result (from backend)
  readonly lastRoll = signal<number | null>(null);
  readonly lastWon = signal<boolean | null>(null);

  // Won item image (stored separately so it persists during won state)
  readonly wonItemImage = signal<string | null>(null);

  // Won skins info (stored separately so it persists during won state display)
  readonly wonSkins = signal<Skin[]>([]);
  readonly wonSkinsPrice = signal<number>(0);

  // Lost amount (wager stored before reset)
  readonly lostAmount = signal<number>(0);

  // ============ Computed Signals ============
  readonly hasSelectedTargets = computed(() => this.selectedTargetSkins().length > 0);

  readonly selectedTargetSkinsPrice = computed(() =>
    this.selectedTargetSkins().reduce((sum, skin) => sum + skin.price, 0)
  );

  readonly totalWagerValue = computed(() => this.wagerAmount());

  readonly maxWagerAmount = computed(() => {
    const targetPrice = this.selectedTargetSkinsPrice();
    const balance = this.session.currentBalance();
    return targetPrice <= 0 ? balance : Math.min(targetPrice, balance);
  });

  // Calculate chance based on wager vs target (with 10% house edge)
  readonly baseChance = computed(() => {
    const targetPrice = this.selectedTargetSkinsPrice();
    const wagerValue = this.totalWagerValue();

    if (targetPrice <= 0 || wagerValue <= 0) return 0;

    const chance = (wagerValue / targetPrice) * 100 * 0.9;
    return Math.min(chance, 90); // Cap at 90%
  });

  // Win range positions - must match backend logic
  // Backend only understands 'under' (0 to chance) or 'over' (100-chance to 100)
  // offset < 0.5 = 'under', offset >= 0.5 = 'over'
  readonly winRangeStart = computed(() => {
    const chance = this.baseChance();
    // 'under' = win zone starts at 0, 'over' = win zone starts at 100-chance
    return this.offset() < 0.5 ? 0 : 100 - chance;
  });
  readonly winRangeEnd = computed(() => {
    const chance = this.baseChance();
    // 'under' = win zone ends at chance, 'over' = win zone ends at 100
    return this.offset() < 0.5 ? chance : 100;
  });

  // Multiplier for display
  readonly multiplier = computed(() => {
    const targetPrice = this.selectedTargetSkinsPrice();
    const wagerValue = this.totalWagerValue();
    return targetPrice > 0 && wagerValue > 0 ? targetPrice / wagerValue : 0;
  });

  readonly isValid = computed(() => {
    const hasTargets = this.hasSelectedTargets();
    const wagerValue = this.totalWagerValue();
    const targetPrice = this.selectedTargetSkinsPrice();

    return hasTargets && wagerValue > 0 && wagerValue <= targetPrice;
  });

  readonly isIdle = computed(() => this.state() === 'idle');
  readonly isResultState = computed(() => this.state() === 'won' || this.state() === 'lost');

  constructor() {
    this.initSocketListeners();
  }

  private initSocketListeners(): void {
    fromEvent<string>(this.socketService.socket, 'upgrader_seed')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(seed => this.clientSeed.set(seed));

    // Re-request seed on socket reconnection to handle cases where
    // the initial request was lost due to socket not being connected yet
    fromEvent(this.socketService.socket, 'connect')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requestNewSeed());
  }

  // ============ Wager Methods ============
  setWagerAmount(amount: number): void {
    const clamped = Math.max(0, Math.min(amount, this.maxWagerAmount()));
    this.wagerAmount.set(clamped);
  }

  halveWager(): void {
    this.setWagerAmount(this.wagerAmount() / 2);
  }

  doubleWager(): void {
    this.setWagerAmount(this.wagerAmount() * 2);
  }

  maxWager(): void {
    this.setWagerAmount(this.maxWagerAmount());
  }

  addToWager(value: number): void {
    this.setWagerAmount(this.wagerAmount() + value);
  }

  // ============ Control Methods ============
  setOffset(value: number): void {
    this.offset.set(Math.max(0, Math.min(1, value)));
  }

  toggleFastMode(): void {
    this.isFastMode.update(v => !v);
  }

  // ============ Target Skin Methods ============
  selectTargetSkin(skin: Skin): void {
    const current = this.selectedTargetSkins();
    const exists = current.some(s => s.assetid === skin.assetid);

    this.selectedTargetSkins.set(
      exists
        ? current.filter(s => s.assetid !== skin.assetid)
        : [...current, skin]
    );
  }

  clearTargetSkins(): void {
    this.selectedTargetSkins.set([]);
  }

  filterTargetSkinsForBalance(): void {
    const balanceType = this.session.balanceType();
    const pureSkinNames = this.session.pureSkins();
    const selected = this.selectedTargetSkins();

    const filtered = selected.filter((skin) => {
      const isPure = pureSkinNames.includes(skin.name);
      if (balanceType === 'skins') return !isPure;
      if (balanceType === 'pure') return isPure;
      return true;
    });

    if (filtered.length !== selected.length) {
      this.selectedTargetSkins.set(filtered);
      const msg = balanceType === 'skins'
        ? "You can't use skins balance to upgrade into pure items"
        : "You can't use pure balance to upgrade into skins items";
      this.toastrService.error(msg);
    }
  }

  /**
   * Reset all state when leaving the page
   */
  resetAll(): void {
    this.selectedTargetSkins.set([]);
    this.wagerAmount.set(0);
    this.offset.set(0.5);
    this.state.set('idle');
    this.isProcessing.set(false);
    this.lastRoll.set(null);
    this.lastWon.set(null);
    this.wonItemImage.set(null);
    this.wonSkins.set([]);
    this.wonSkinsPrice.set(0);
    this.lostAmount.set(0);
  }

  // ============ State Management ============
  private resetState(): void {
    this.state.set('idle');
    this.lastRoll.set(null);
    this.lastWon.set(null);
    this.wonItemImage.set(null);
    this.wonSkins.set([]);
    this.wonSkinsPrice.set(0);
    this.lostAmount.set(0);
  }

  // ============ Upgrade Flow ============
  async upgrade(): Promise<{ won: boolean; roll: number } | null> {
    if (!this.isValid() || this.isProcessing()) {
      return null;
    }

    this.isProcessing.set(true);

    // Store the target skins info before rolling (so it's available on win display)
    const targetSkins = this.selectedTargetSkins();
    this.wonSkins.set(targetSkins);
    this.wonSkinsPrice.set(this.selectedTargetSkinsPrice());
    this.wonItemImage.set(targetSkins[0]?.image ?? null);
    this.lostAmount.set(this.wagerAmount());

    try {
      const { won, roll } = await this.executeBackendUpgrade();

      this.lastRoll.set(roll);
      this.lastWon.set(won);
      this.state.set('rolling');

      return { won, roll };
    } catch (error) {
      // Reset state on error (same as old upgrader)
      this.state.set('idle');
      this.isProcessing.set(false);
      return null;
    }
  }

  private async executeBackendUpgrade(): Promise<{ won: boolean; roll: number }> {
    // Use the same API as the old upgrader
    // offset < 0.5 = 'under', offset >= 0.5 = 'over'
    const type = this.offset() < 0.5 ? 'under' : 'over';

    const [won, percentage] = await this.socketService.emitArgs('upgrader_use', {
      amount: +this.wagerAmount().toFixed(2),
      upgrade: this.selectedTargetSkinsPrice(),
      type,
      balance: this.session.balanceType(),
    });

    // percentage from backend is the roll result (0-100)
    return { won, roll: percentage };
  }

  requestNewSeed(): void {
    this.socketService.socket.emit('upgrader_seed', null);
  }

  finishAnimation(): void {
    this.isProcessing.set(false);
  }

  /** Reset left/right panels immediately (without resetting hexagon state) */
  resetPanels(won: boolean): void {
    if (won) {
      // Move target skins to user inventory display
      const wonSkins = this.selectedTargetSkins();
      this.userInventorySkins.update(skins => [...wonSkins, ...skins]);
    }

    this.requestNewSeed();

    this.selectedTargetSkins.set([]);
    this.wagerAmount.set(0);
  }

  /** Called after win/lose display timeout to reset hexagon state */
  completeRound(): void {
    this.resetState();
  }

  /**
   * Load skins available for deposit to the upgrader.
   * Uses a 4-second timeout so the promise never hangs if the server
   * hasn't finished authenticating yet (e.g. on page reload).
   * Throws on timeout/error so the caller can retry.
   */
  async loadDepositSkins(): Promise<UpgraderSkin[]> {
    const TIMEOUT_MS = 4000;

    const response = await new Promise<any>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), TIMEOUT_MS);
      this.socketService.socket.emit('load_site_inventory', (err: any, data: any) => {
        clearTimeout(timer);
        if (err) reject(new Error(err));
        else resolve(data);
      });
    });

    if (Array.isArray(response)) {
      return response.map((item: any) => ({
        ...item,
        name: item.market_hash_name || item.name || '',
        image: normalizeSkinImage(item.image),
        color: item.color || item.name_color || DEFAULT_SKIN_COLOR,
      }));
    }
    return [];
  }
}
